/**
 * Agent Management Service
 * 
 * Handles all agent-related API calls including listing,
 * creating, updating, and deleting agents
 */

import { api, handleApiError } from './api';
import { statusToAPI, statusFromAPI } from '@/utils';
import type {
  AgentListItem,
  AgentFilters,
  CreateAgentPayload,
  UpdateAgentPayload,
  PaginationParams,
  PaginatedResponse,
} from '@/types';

/**
 * Agent Service Object
 * Contains all agent management API operations
 */
export const agentService = {
  /**
   * Get Agents List
   * Fetches paginated list of agents with optional filters
   * 
   * @param params - Pagination parameters
   * @param filters - Optional filters for agents
   * @returns Promise with paginated agent list
   */
  getAgents: async (
    params: PaginationParams,
    filters?: AgentFilters
  ): Promise<PaginatedResponse<AgentListItem>> => {
    try {
      // Debug log para verificar os parâmetros recebidos
      if (import.meta.env.DEV) {
        console.log('🚀 agentService.getAgents called with:', {
          params,
          filters,
          hasHostname: !!filters?.hostname,
          hostnameValue: filters?.hostname,
        });
      }

      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        size: params.size.toString(),
        ...(params.sort && { sort: params.sort }),
        // Using hostname filter instead of general search
        ...(filters?.hostname && { hostname: filters.hostname }),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.organizationRef && { organizationRef: filters.organizationRef }),
        ...(filters?.os && { os: filters.os }),
        ...(filters?.lastSeenFrom && { lastSeenFrom: filters.lastSeenFrom }),
        ...(filters?.lastSeenTo && { lastSeenTo: filters.lastSeenTo }),
        ...(filters?.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters?.dateTo && { dateTo: filters.dateTo }),
      });

      // Debug log da URL final
      if (import.meta.env.DEV && filters?.hostname) {
        console.log('🌐 Final API URL:', `/agents?${queryParams.toString()}`);
      }

      const response = await api.get<PaginatedResponse<AgentListItem>>(
        `/agents?${queryParams.toString()}`
      );

      // Log para debug (remover em produção)
      if (import.meta.env.DEV) {
        console.log('📊 Agents API Response:', response.data);
      }

      // Verificar se a resposta tem o formato esperado
      if (!response.data) {
        throw new Error('Invalid response format: missing data');
      }

      // Normalizar resposta do Spring Data Pageable para nosso formato
      const responseData = response.data as any;
      
      // Se a resposta tem o formato Spring Data (content, pageable, totalPages)
      if (responseData.content && responseData.pageable) {
        // Normalizar dados dos agentes (converter status para lowercase)
        const normalizedAgents = responseData.content.map((agent: any) => ({
          ...agent,
          id: agent.id?.toString() || agent.id, // Garantir que ID é string
          status: statusFromAPI(agent.status || 'OFFLINE'), // Converter status para lowercase
        }));

        return {
          data: normalizedAgents,
          pagination: {
            page: responseData.number || 0,
            size: responseData.size || params.size,
            total: responseData.totalElements || 0,
            totalPages: responseData.totalPages || 1,
            hasNext: !responseData.last,
            hasPrev: !responseData.first,
          },
        };
      }

      // Se a resposta não tiver a estrutura de paginação, criar uma
      if (!response.data.pagination) {
        const items = Array.isArray(response.data) ? response.data : [];
        return {
          data: items,
          pagination: {
            page: params.page,
            size: params.size,
            total: items.length,
            totalPages: Math.ceil(items.length / params.size) || 1,
            hasNext: params.page < Math.ceil(items.length / params.size) - 1,
            hasPrev: params.page > 0,
          },
        };
      }

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Agent by ID
   * Fetches detailed information about a specific agent
   * 
   * @param id - Agent ID
   * @returns Promise with agent details
   */
  getAgentById: async (id: string): Promise<AgentListItem> => {
    try {
      const response = await api.get<AgentListItem>(`/agents/${id}`);
      
      // Normalize status from API response
      const agent = response.data;
      return {
        ...agent,
        status: agent.status ? statusFromAPI(agent.status) as any : undefined,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Agent by Reference (UUID)
   * Fetches detailed information about a specific agent using reference
   * More secure than using ID as it doesn't expose sequential IDs
   * 
   * @param reference - Agent reference (UUID)
   * @returns Promise with agent details
   */
  getAgentByReference: async (reference: string): Promise<AgentListItem> => {
    try {
      const response = await api.get<AgentListItem>(`/agents/reference/${reference}`);
      
      // Normalize status from API response
      const agent = response.data;
      return {
        ...agent,
        status: agent.status ? statusFromAPI(agent.status) as any : undefined,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Create Agent
   * Creates a new agent in the system
   * 
   * @param agentData - Agent data for creation
   * @returns Promise with created agent
   */
  createAgent: async (agentData: CreateAgentPayload): Promise<AgentListItem> => {
    try {
      // Convert status to uppercase for API
      const apiPayload = {
        ...agentData,
        status: agentData.status ? statusToAPI(agentData.status) : 'OFFLINE',
      };
      
      const response = await api.post<AgentListItem>('/agents', apiPayload);
      
      // Normalize status from API response
      const agent = response.data;
      return {
        ...agent,
        status: agent.status ? statusFromAPI(agent.status) as any : undefined,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Update Agent
   * Updates an existing agent's information using reference (UUID)
   * 
   * @param ref - Agent reference (UUID)
   * @param agentData - Partial agent data to update
   * @returns Promise with updated agent
   */
  updateAgent: async (
    ref: string,
    agentData: UpdateAgentPayload
  ): Promise<AgentListItem> => {
    try {
      // Convert status to uppercase for API if provided
      const apiPayload = {
        ...agentData,
        ...(agentData.status && { status: statusToAPI(agentData.status) }),
      };
      
      const response = await api.put<AgentListItem>(
        `/agents/${ref}`,
        apiPayload
      );
      
      // Normalize status from API response
      const agent = response.data;
      return {
        ...agent,
        status: agent.status ? statusFromAPI(agent.status) as any : undefined,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Delete Agent
   * Deletes an agent from the system using reference (UUID)
   * 
   * @param ref - Agent reference (UUID)
   * @returns Promise that resolves when agent is deleted
   */
  deleteAgent: async (ref: string): Promise<void> => {
    try {
      await api.delete(`/agents/${ref}`);
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Update Agent Status
   * Changes agent status (online, offline, inactive, error)
   * 
   * @param id - Agent ID
   * @param status - New status
   * @returns Promise with updated agent
   */
  updateAgentStatus: async (
    id: string,
    status: string
  ): Promise<AgentListItem> => {
    try {
      // Convert status to uppercase for API
      const response = await api.patch<AgentListItem>(
        `/agents/${id}/status`,
        { status: statusToAPI(status) }
      );
      
      // Normalize status from API response
      const agent = response.data;
      return {
        ...agent,
        status: agent.status ? statusFromAPI(agent.status) as any : undefined,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Bulk Delete Agents
   * Deletes multiple agents at once
   * 
   * @param ids - Array of agent IDs to delete
   * @returns Promise that resolves when agents are deleted
   */
  bulkDeleteAgents: async (ids: string[]): Promise<void> => {
    try {
      await api.post('/agents/bulk-delete', { ids });
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Export Agents
   * Exports agents list to CSV/Excel
   * 
   * @param filters - Optional filters for export
   * @returns Promise with file blob
   */
  exportAgents: async (filters?: AgentFilters): Promise<Blob> => {
    try {
      const queryParams = new URLSearchParams({
        ...(filters?.status && { status: filters.status }),
        ...(filters?.organizationRef && { organizationRef: filters.organizationRef }),
        ...(filters?.os && { os: filters.os }),
        ...(filters?.lastSeenFrom && { lastSeenFrom: filters.lastSeenFrom }),
        ...(filters?.lastSeenTo && { lastSeenTo: filters.lastSeenTo }),
        ...(filters?.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters?.dateTo && { dateTo: filters.dateTo }),
      });

      const response = await api.get(
        `/agents/export?${queryParams.toString()}`,
        {
          responseType: 'blob',
        }
      );

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },
};

export default agentService;
